import type { ShopData } from "@/utils/storage";
import {
  clearBrowserCache,
  getCurrentShopData,
  setCurrentShopData,
  setGlobalCurrentShopData,
} from "@/utils/storage";
import {
  ClearOutlined,
  LogoutOutlined,
  RetweetOutlined,
  SettingOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { history, useModel } from "@umijs/max";
import type { RadioChangeEvent } from "antd";
import { Avatar, Modal, Radio, Space, Spin } from "antd";
import type { ItemType } from "antd/lib/menu/hooks/useItems";
import type { MenuInfo } from "rc-menu/lib/interface";
import React, { useCallback, useEffect, useState } from "react";
import HeaderDropdown from "../HeaderDropdown";
import styles from "./index.less";
import { useLogout } from "@/hooks/useLogout";
import { PAGES } from "@/constant/pages";
import { sendLog } from "@/utils/tracker";

export type GlobalHeaderRightProps = {
  menu?: boolean;
};

const AvatarDropdown: React.FC<GlobalHeaderRightProps> = ({ menu }) => {
  const { initialState, refresh, setInitialState } = useModel("@@initialState");
  const { resetModalModel } = useModel("useModalModel");
  const [currentShop, setCurrentShop] = useState<ShopData>({});
  const [shopId, setShopId] = useState(0);
  const [showShopExchange, setShowShopExchange] = useState(false);
  const logout = useLogout();

  const handleShopChange = (event: RadioChangeEvent) => {
    setShopId(event.target.value);
  };

  const handleConfirmShopChange = async () => {
    if (shopId === currentShop.shopId) {
      setShowShopExchange(false);
      return;
    }
    const shop: ShopData =
      initialState?.currentUser?.suppliers?.find(
        (item) => item.shopId === shopId,
      ) ?? {};
    setCurrentShopData(shop);
    setGlobalCurrentShopData(shop);
    setInitialState((s) => ({ ...s, currentShop: shop }));
    await refresh();
    /**
     * 清除弹窗弹出数据
     */
    resetModalModel();
    setShowShopExchange(false);
    window.location.replace("/");
  };

  const handleCancel = () => {
    setShowShopExchange(false);
    setShopId(currentShop.shopId || 0);
  };

  const onMenuClick = useCallback(
    (event: MenuInfo) => {
      const { key } = event;
      // 退出登录
      if (key === "logout") {
        sendLog("pcMainFloatingFrame", "主悬浮框", {
          click_source: "退出登录",
        });
        logout();
        return;
      }
      // 切换店铺
      if (key === "exchange") {
        setShowShopExchange(true);
        sendLog("pcMainFloatingFrame", "主悬浮框", {
          click_source: "切换店铺",
        });
        return;
      }
      // 清除缓存
      if (key === "clear") {
        clearBrowserCache();
        window.location.reload();
        return;
      }
      //个人信息
      if (key === "account") {
        history.push(`${PAGES.PERSONAL_ACCOUNT}`);
        sendLog("pcMainFloatingFrame", "主悬浮框", {
          click_source: "账号信息",
        });
        history.push(`${PAGES.PERSONAL_ACCOUNT}`);
        return;
      }
      history.push(`/account/${key}`);
    },
    [logout],
  );

  const loading = (
    <span className={`${styles.action} ${styles.account}`}>
      <Spin
        size="small"
        style={{
          marginLeft: 8,
          marginRight: 8,
        }}
      />
    </span>
  );

  useEffect(() => {
    try {
      const shop: ShopData = getCurrentShopData();
      setCurrentShop(shop);
      setShopId(shop.shopId || 0);
    } catch (e) {}
  }, []);

  // useEffect(() => {
  //   if (!needAuthentication) {
  //     return;
  //   }
  //   Modal.confirm({
  //     keyboard: false,
  //     maskClosable: false,
  //     closable: false,
  //     title: "店铺鉴权提示",
  //     content: "该店铺未完成爆版APP实名鉴权，请先登录「爆版APP」完成实名鉴权",
  //     cancelText: "退出登录",
  //     okText: "切换店铺",
  //     onCancel: () => {
  //       /**
  //        * 生产环境和预发布环境执行退出登录
  //        * 测试和开发环境只本地清空登录信息
  //        */
  //       if (REACT_APP_ENV === "pre" || REACT_APP_ENV === "prod") {
  //         logout().then(() => {
  //           resetInitialAuthentication();
  //           navLogin();
  //         });
  //       } else {
  //         resetInitialAuthentication();
  //         navLogin();
  //       }
  //     },
  //     onOk: () => {
  //       setShowShopExchange(true);
  //       return Promise.reject();
  //     },
  //   });
  // }, [needAuthentication, resetInitialAuthentication]);

  if (!initialState) {
    console.log("---no initialState");
    return loading;
  }

  const { currentUser, currentShop: initCurrentShop } = initialState;

  // 全局状态有修改时 判断店铺名是否有修改
  if (currentShop.shopName !== initCurrentShop?.shopName) {
    setCurrentShop({
      ...currentShop,
      shopName: initCurrentShop?.shopName,
    });
  }

  if (!currentUser || !currentUser.userId) {
    console.log("---no currentUser", currentUser);
    return loading;
  }

  const menuItems: ItemType[] = [
    ...(menu
      ? [
          {
            key: "center",
            icon: <UserOutlined />,
            label: "个人中心",
          },
          {
            key: "settings",
            icon: <SettingOutlined />,
            label: "个人设置",
          },
          {
            type: "divider" as const,
          },
        ]
      : []),
    {
      key: "account",
      icon: <UserOutlined />,
      label: "账号信息",
    },
    {
      key: "exchange",
      icon: <RetweetOutlined />,
      label: "切换店铺",
    },
    {
      key: "clear",
      icon: <ClearOutlined />,
      label: "清除缓存",
    },
    {
      type: "divider" as const,
    },
    {
      key: "logout",
      icon: <LogoutOutlined />,
      label: "退出登录",
    },
  ];

  return (
    <>
      <HeaderDropdown
        menu={{
          items: menuItems,
          onClick: onMenuClick,
        }}
      >
        <span className={`${styles.action} ${styles.account}`}>
          <Avatar
            size="small"
            className={styles.avatar}
            src={currentUser.logo}
            alt="avatar"
          />
          <span className={styles.name}>
            <span className={`${styles["shop-name"]} anticon`}>
              {currentShop.shopName}
            </span>
            <span className={`${styles["user-name"]} anticon`}>
              {currentUser.userName}
            </span>
          </span>
        </span>
      </HeaderDropdown>
      <Modal
        open={showShopExchange}
        title="请选择切换店铺"
        onOk={handleConfirmShopChange}
        onCancel={handleCancel}
        zIndex={2000}
      >
        <Radio.Group onChange={handleShopChange} value={shopId}>
          <Space direction="vertical">
            {initialState?.currentUser?.suppliers?.map((item) => (
              <Radio key={item.shopId} value={item.shopId}>
                {item.shopName}
              </Radio>
            ))}
          </Space>
        </Radio.Group>
      </Modal>
    </>
  );
};

export default AvatarDropdown;
